import React, { useEffect, useRef } from 'react';
import { BundleBreakdownList } from './BundleContents';
import { VatLines } from './VatLines';
import { homeDeliveryWhen } from '../../api/_lib/deliveryText';
import { CheckCircle, Mail, Wallet, Truck, Smartphone } from 'lucide-react';
import { QrCode } from '../ui/QrCode';
import { formatSwishNumber } from '../../services/db/users';
import type { OrderReceipt } from '../../services/db/orders';

interface OrderSuccessProps {
    receipt: OrderReceipt | null;
    onReset: () => void;
}

const formatKr = (value: number) => `${value.toLocaleString('sv-SE')} kr`;

export const OrderSuccess: React.FC<OrderSuccessProps> = ({ receipt, onReset }) => {
    const headingRef = useRef<HTMLHeadingElement>(null);

    // Flytta fokus till bekräftelsen så skärmläsare annonserar att köpet gick igenom.
    useEffect(() => {
        headingRef.current?.focus();
        window.scrollTo({ top: 0 });
    }, []);

    const sellerFirstName = receipt?.sellerName?.split(' ')[0] || 'säljaren';
    const isHome = receipt?.deliveryMethod === 'HOME';
    // Betalt i kassan (hemleverans eller hämtning), Swish till säljaren eller direkt till säljaren.
    const paidInCheckout = receipt?.paymentMethod === 'STRIPE' || isHome;
    const swish = receipt?.paymentMethod === 'SWISH' && receipt.sellerSwish ? receipt.sellerSwish : null;
    const shipping = receipt?.shippingAmount || 0;
    const total = receipt?.totalAmount === null || receipt?.totalAmount === undefined ? null : receipt.totalAmount + shipping;
    const heading = paidInCheckout
        ? `Tack${receipt?.customerFirstName ? `, ${receipt.customerFirstName}` : ''}! ${receipt?.paymentState === 'processing' ? 'Betalningen behandlas.' : 'Betalningen är klar.'}`
        : `Tack${receipt?.customerFirstName ? `, ${receipt.customerFirstName}` : ''}! Beställningen är skickad.`;
    const pickupWhen = receipt?.campaignEnd
        ? `när ${receipt.className || 'gruppen'} fått sin leverans efter ${new Date(receipt.campaignEnd).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })}`
        : 'när gruppen fått sin leverans efter säljperioden';

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 py-10">
        <div className="bg-white p-6 sm:p-10 rounded-3xl shadow-2xl border border-stone-100 max-w-md w-full animate-fade-in-up">
          <div className="text-center">
            <div className="mx-auto bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mb-6 border border-green-100 shadow-sm">
              <CheckCircle className="w-10 h-10 text-green-600" aria-hidden="true" />
            </div>
            <h2 ref={headingRef} tabIndex={-1} className="text-2xl sm:text-3xl font-heading font-bold text-coffee-900 mb-2 outline-none text-balance">
              {heading}
            </h2>
            {receipt && (
              <p className="text-sm text-stone-500 font-medium">
                Ordernummer <span className="font-mono font-bold text-stone-800">{receipt.orderNumber}</span>
              </p>
            )}
          </div>

          {receipt && receipt.items.length > 0 && (
            <div className="mt-8 border-y border-stone-100 py-4">
              <ul className="space-y-2">
                {receipt.items.map((item, idx) => (
                  <li key={idx} className="flex justify-between gap-3 text-sm">
                    <div className="text-stone-700 min-w-0 flex-1">
                      {item.quantity} × {item.name}
                      {item.variantName && <span className="text-stone-400"> – {item.variantName}</span>}
                      {item.bundle
                        ? <BundleBreakdownList breakdown={{ rows: item.bundle.parts, ordinary: 0, discount: item.bundle.discount }} className="mt-1 text-xs" />
                        : item.contents && <span className="block text-xs text-stone-400">Innehåller {item.contents}</span>}
                    </div>
                    <span className="font-bold text-stone-800 whitespace-nowrap">{formatKr(item.price * item.quantity)}</span>
                  </li>
                ))}
              </ul>
              {isHome && (
                <div className="flex justify-between gap-3 text-sm mt-2">
                  <span className="text-stone-700">Frakt</span>
                  <span className="font-bold text-stone-800 whitespace-nowrap">{formatKr(shipping)}</span>
                </div>
              )}
              {total !== null && (
                <div className="flex justify-between mt-4 pt-3 border-t border-dashed border-stone-200">
                  <span className="text-xs font-black uppercase tracking-widest text-stone-500">{paidInCheckout ? 'Betalt' : `Att betala till ${sellerFirstName}`}</span>
                  <span className="text-lg font-bold text-coffee-900">{formatKr(total)}</span>
                </div>
              )}
              {paidInCheckout && <VatLines lines={receipt.vatBreakdown} className="mt-1" />}
            </div>
          )}

          {swish && receipt && total !== null && (
            // Swish till säljaren: numret, beloppet och ordernumret som meddelande, plus QR för Swish-appen.
            <div className="mt-6 rounded-2xl bg-stone-900 text-white p-5 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/60 flex items-center gap-1.5"><Smartphone size={12} aria-hidden="true" /> Swisha {receipt.sellerName}</p>
                <p className="font-mono text-xl font-bold mt-1 whitespace-nowrap">{formatSwishNumber(swish)}</p>
                <p className="text-sm mt-1"><strong>{formatKr(total)}</strong> · meddelande <span className="font-mono font-bold">{receipt.orderNumber}</span></p>
              </div>
              <div className="bg-white p-1.5 rounded-lg flex-shrink-0">
                <QrCode data={`C${swish};${total};${receipt.orderNumber};0`} className="w-24 h-24" label="QR-kod för Swish" />
              </div>
            </div>
          )}

          <ul className="mt-6 space-y-4 text-sm text-stone-600 leading-relaxed">
            <li className="flex gap-3">
              <Wallet size={18} className="text-coffee-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                <strong className="text-stone-800">Betalning:</strong>{' '}
                {paidInCheckout
                  ? <>{receipt?.paymentState === 'processing' ? 'Betalningen behandlas av Stripe. Du får ett mejl när den är klar.' : 'Betald via Stripe till CoffeeNova. Du behöver inte betala säljaren.'} Ditt köp stöttar {receipt?.className || 'gruppen'}.</>
                  : swish
                    ? <>Swisha {total !== null ? formatKr(total) : ''} till {receipt?.sellerName} med ordernumret som meddelande. Öppna Swish och skanna QR-koden, eller skriv in numret.</>
                    : <>Betala {total !== null ? formatKr(total) : ''} till {receipt?.sellerName || sellerFirstName} direkt, när ni ses eller som ni kommer överens.</>}
              </span>
            </li>
            <li className="flex gap-3">
              <Truck size={18} className="text-coffee-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                {isHome
                  ? <><strong className="text-stone-800">Leverans:</strong> Vi skickar ditt kaffe{receipt?.shippingAddress ? <> till <span className="text-stone-800">{receipt.shippingAddress}</span></> : ''} {homeDeliveryWhen(receipt?.campaignEnd)}. Du får ett mejl med spårningsnummer när paketet lämnat oss.</>
                  : <><strong className="text-stone-800">Leverans:</strong> {sellerFirstName} lämnar ut kaffet {pickupWhen}.</>}
              </span>
            </li>
            {receipt && (
              <li className="flex gap-3">
                <Mail size={18} className="text-coffee-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  {paidInCheckout
                    ? <>Kvitto och orderbekräftelse skickas till <strong className="text-stone-800 break-all">{receipt.customerEmail}</strong>.</>
                    : receipt.confirmationSent
                    ? <>En bekräftelse har skickats till <strong className="text-stone-800 break-all">{receipt.customerEmail}</strong>.</>
                    : <>Spara ordernumret ovan. Du kan alltid se din beställning under <a href="#/my-orders" className="underline font-bold text-stone-800">Spåra order</a>.</>}
                </span>
              </li>
            )}
          </ul>

          <button
            type="button"
            onClick={onReset}
            className="mt-8 w-full py-4 bg-coffee-900 text-white text-xs font-black rounded-xl hover:bg-coffee-800 transition-all shadow-lg uppercase tracking-widest"
          >
            Tillbaka till butiken
          </button>
          <p className="mt-4 text-center text-[11px] text-stone-400">
            <a href="#/info/terms" className="underline">Köpvillkor</a> · <a href={`#/angra${receipt ? `?order=${receipt.orderNumber}&email=${encodeURIComponent(receipt.customerEmail)}` : ''}`} className="underline">Ångra köpet här</a>
          </p>
        </div>
      </div>
    );
};
